import { useRef } from 'react';

import cn from 'classnames/bind';
import PropTypes from 'prop-types';
import { BorderedItem, Accordion, ArrowUp, DynamicComponent } from 'ui';
import SectionObserver from 'ui/layouts/SectionObserver';
import { classNameType, colorType } from 'utils/types';

import s from './PageContentMenuTree.module.scss';

const cx = cn.bind(s);

const List = ({
  items, color, active, nestingLevel = 0, variant
}) => {
  const hasAccordion = variant !== 'blog'
  const isRoot = nestingLevel === 0;
  const hasGroupInSiblings = items.find((item) => item.children?.length);
  const activeClassName = variant === 'blog' ? '' : undefined
  return (
    <ul
      className={cx('list', {
        'pl-0': !isRoot,
        'mt-[10px]': !isRoot && variant !== 'blog',
        'mt-[15px]': !isRoot && variant === 'blog',
        nestedList: !isRoot,
        rootList: isRoot && hasAccordion,
        hasLeftOffset: nestingLevel === 1 && hasAccordion,
        hasLeftBlogOffset: nestingLevel === 1 && !hasAccordion
      })}
    >
      {items.map((item) => (
        <DynamicComponent tag={hasAccordion ? Accordion : 'div'}  key={item.title} {...(hasAccordion ? {initialIsOpen: true} : {})}>
          <ListItem
            item={item}
            hasGroupInSiblings={hasGroupInSiblings}
            active={active}
            color={color}
            isRoot={isRoot}
            nestingLevel={nestingLevel}
            variant={variant}
            activeClassName={activeClassName}
            hasAccordion={hasAccordion}
          />
        </DynamicComponent>
      ))}
    </ul>
  );
};
const stopPropagationListener = (e) => e?.stopPropagation?.();
const ListItem = ({
  item, hasGroupInSiblings, active, color, isRoot, nestingLevel, activeClassName = 'activeItem', hasAccordion, variant
}) => {
  const hasChildren = !!item?.children?.length;
  const isAccordion = hasChildren && hasAccordion
  const isGroupItem = hasChildren || hasGroupInSiblings;
  const isActive = item.key === active;
  const onClick = useRef(hasChildren ? stopPropagationListener : null);

  const link = (<SectionObserver.Link href={item.href || '#'} className={cx('link')} onClick={onClick.current}>
    {item.menuTitle || item.title}
  </SectionObserver.Link>);
  return (
    <li
      key={item.title}
      className={cx('item', {
        'tp-text-small': variant !== 'blog' || isGroupItem,
        'font-semibold': variant !== 'blog' && isGroupItem,
        'font-serif': variant === 'blog',
        'font-normal text-[14px] leading-[23.5px]': variant === 'blog' && !isGroupItem,
        'font-bold leading-[27px]': variant === 'blog' && isGroupItem,
        [activeClassName]: isActive,
        'mb-[10px]': !isRoot && !isGroupItem && variant !== 'blog',
        'mb-[17px]': !isRoot && variant === 'blog',
        'mb-10': isRoot && variant !== 'blog',
        'mt-5 mb-0': !isRoot && isGroupItem && variant !== 'blog',
        'mb-2': isRoot && variant === 'blog',
      })}
    >
      <BorderedItem
        as={isAccordion ? Accordion.Header : 'span'}
        color={isActive || isGroupItem ? color : 'grey'}
        height="small"
        active={!isGroupItem || isActive}
        className={cx('borderedItem')}
        hasHoverEffect={!isGroupItem}
      >
        {isAccordion ? (isOpened) => (
          <>
            <span className={cx('arrow')}><ArrowUp isOpen={isOpened} /></span>
            {link}
          </>
        ) : link}

      </BorderedItem>
      {item.children && (
        <Accordion.Content>
          <List
            items={item.children}
            active={active}
            color={color}
            nestingLevel={nestingLevel + 1}
            variant={variant}
          />
        </Accordion.Content>
      )}
    </li>
  );
};
const PageContentMenuTree = ({
  items, title, color, active, variant
}) => (
  <>
    {title && <h3 className={cx('title', 'tp-quotes-small')}>{title}</h3>}
    <List items={items} active={active} color={color} isRoot variant={variant} />
  </>
);

const itemPropType = {
  title: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};
itemPropType.children = PropTypes.arrayOf(PropTypes.shape(itemPropType));

PageContentMenuTree.propTypes = {
  className: classNameType,
  title: PropTypes.string,
  color: colorType,
  active: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape(itemPropType)),
};

export default PageContentMenuTree;
